import React, { useEffect, useState, useContext } from 'react'
import { useAuth } from '../../hooks/auth'
import { Navigate } from 'react-router-dom'
import ColumnChart from '../Hub/components/DashboardCharts/ColumnChart'
import LineChart from '../Hub/components/DashboardCharts/LineChart'
import PieChartPlanCount from './components/DashboardCharts/PieChartPlanCount'
import PieChartPlanSumm from './components/DashboardCharts/PieChartPlanSumm'
import PieChartPlanCountMSP from './components/DashboardCharts/PieChartPlanCountMSP'
import PieChartPlanSummMSP from './components/DashboardCharts/PieChartPlanSummMSP'
import PieChartPlanCountGWS from './components/DashboardCharts/PieChartPlanCountGWS'
import PieChartPlanSummGWS from './components/DashboardCharts/PieChartPlanSummGWS'
import PieChartPlanCountForm from './components/DashboardCharts/PieChartPlanCountForm'
import PieChartPlanSummType from './components/DashboardCharts/PieChartPlanSummType'
import TableChartFHD from './components/DashboardCharts/TableChartFHD'
import TableChartPurchaseMethod from './components/DashboardCharts/TableChartPurchaseMethod'
import TableChartPurchaseStruct from './components/DashboardCharts/TableChartPurchaseStruct'
import PositionsStageCount from './components/DashboardCharts/PositionsStageCount'

import Footer from '../Home/components/Footer'

import NumberFormat from './components/DashboardCharts/NumberFormat'

import { Plan } from './Plans/types'

import { ThemeContext } from '../../components/ThemeContext'

type ApiResponse = {
    message: Plan[]
}

const HubPage: React.FC = () => {
    const [guid, setGuid] = useState<string>('Нет данных')
    const [registrationNumber, setRegistrationNumber] =
        useState<string>('Нет данных')
    const [availableYears, setAvailableYears] = useState<number[]>([])
    const [selectedYear, setSelectedYear] = useState<string | undefined>(
        'Нет данных'
    )
    const [countPlan, setCountPlan] = useState('Нет данных')
    const [sumPlan, setSumPlan] = useState('Нет данных')
    const [sumMSPPlanPercent, setSumMSPPlanPercent] = useState('Нет данных')
    const [sumMSPPlan, setSumMSPPlan] = useState('Нет данных')
    const [isLoading, setLoading] = useState(false)

    const { theme } = useContext(ThemeContext)

    const { user, logout } = useAuth()

    const fetchPlans = async (initialYear?: string) => {
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + '/api/backend/v2/plans',
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`)
            }
            const data: ApiResponse = await response.json()
            const plans: Plan[] = data.message

            if (!plans || plans.length === 0) {
                setGuid('Нет данных')
                setRegistrationNumber('Нет данных')
                setAvailableYears([])
                setSelectedYear('Нет данных')
                return
            }

            // Извлечение уникальных годов из start_date
            const years = Array.from(
                new Set(
                    plans
                        .map((plan) => plan.start_date?.split('-')[0])
                        .filter(Boolean)
                )
            )
                .map(Number)
                .sort((a, b) => b - a)

            setAvailableYears(years)

            // Выбор года
            const currentYear = new Date().getFullYear()
            let defaultYear = initialYear

            if (!initialYear) {
                if (years.includes(currentYear)) {
                    defaultYear = currentYear.toString()
                } else if (years.includes(currentYear - 1)) {
                    defaultYear = (currentYear - 1).toString()
                } else {
                    defaultYear = 'Нет данных'
                }
            }

            setSelectedYear(defaultYear)

            // Найти план для выбранного года
            const planForYear = plans.find((plan) =>
                plan.start_date?.startsWith(defaultYear || '')
            )
            if (planForYear) {
                setGuid(planForYear.guid || 'Нет данных')
                setRegistrationNumber(
                    String(planForYear.registration_number) || 'Нет данных'
                )
            } else {
                setGuid('Нет данных')
                setRegistrationNumber('Нет данных')
            }
        } catch (error) {
            console.error('Error fetching plans:', error)
            setGuid('Нет данных')
            setRegistrationNumber('Нет данных')
            setAvailableYears([])
            setSelectedYear('Нет данных')
        }
    }

    const fetchCountPlan = async (reg_number?: string) => {
        setLoading(true)

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v2/plan_positions_count_without_draft/?reg_number=${reg_number}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                setCountPlan('Нет данных')
                setLoading(false)
                throw new Error(`Error: ${response.statusText}`)
            }

            const poses_count = await response.json()

            setCountPlan(poses_count.poses_count)
        } catch (error) {
            console.error('Error fetching data:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchSumPlan = async (reg_number?: string) => {
        setLoading(true)

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v2/plan_positions_summ_piechartplansumm/?reg_number=${reg_number}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                setSumPlan('Нет данных')
                setLoading(false)
                throw new Error(`Error: ${response.statusText}`)
            }

            const poses_summ = await response.json()

            setSumPlan(poses_summ.poses_summ)
        } catch (error) {
            console.error('Error fetching data:', error)
        } finally {
            setLoading(false)
        }
    }

    const fetchSumMSPPlan = async (reg_number?: string) => {
        setLoading(true)

        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL +
                    `/api/backend/v2/plan_positions_count_piechartplansummsmb/?reg_number=${reg_number}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )
            if (!response.ok) {
                setSumMSPPlan('Нет данных')
                setSumMSPPlanPercent('Нет данных')
                setLoading(false)
                throw new Error(`Error: ${response.statusText}`)
            }

            const poses_sum = await response.json()

            // Проверяем, что все необходимые поля существуют
            if (
                typeof poses_sum.poses_sum_smb === 'undefined' ||
                typeof poses_sum.poses_sum_not_smb === 'undefined'
            ) {
                setSumMSPPlan('Нет данных')
                setSumMSPPlanPercent('Нет данных')
                throw new Error('Response does not contain required fields')
            }

            // Проверяем деление на ноль
            if (poses_sum.poses_sum_smb === 0) {
                setSumMSPPlan('Нет данных')
                throw new Error('Division by zero error')
            }

            // Расчёт процента
            const calc =
                (poses_sum.poses_sum_smb * 100) /
                (poses_sum.poses_sum_smb + poses_sum.poses_sum_not_smb)

            setSumMSPPlan(poses_sum.poses_sum_smb)
            setSumMSPPlanPercent(calc.toFixed(2))
        } catch (error) {
            console.error('Error fetching data:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchPlans()
    }, [])

    useEffect(() => {
        if (registrationNumber != 'Нет данных') {
            fetchCountPlan(registrationNumber)
            fetchSumPlan(registrationNumber)
            fetchSumMSPPlan(registrationNumber)
        } else {
            setCountPlan('Нет данных')
            setSumPlan('Нет данных')
            setSumMSPPlan('Нет данных')
            setSumMSPPlanPercent('Нет данных')
        }
    }, [registrationNumber])

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const year = e.target.value
        setSelectedYear(year)
        fetchPlans(year) // Передаём выбранный год в fetchPlans
    }

    const handleLogout = () => {
        fetch(process.env.REACT_APP_API_URL + '/api/auth/v1/users/logout/', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }).then((r) => {
            if (r.ok) {
                logout()
            }
        })
    }

    if (!user) {
        return <Navigate to="/auth" />
    }

    return (
        <div className="flex flex-col w-full h-screen bg-[#f0f3f5] dark:bg-[#181818]">
            <div className="flex flex-row w-full h-24 justify-center items-center bg-[#e5e5e5] dark:bg-[#000000]">
                {/* <button
                    onClick={handleLogout}
                    className="text-[#181818] dark:text-[#FFFFFF] w-fit h-fit bg-slate-800 py-1 px-3 rounded absolute top-8 left-52"
                >
                    Выход
                </button> */}
                <PositionsStageCount
                    reg_number={registrationNumber || 'Нет данных'}
                    theme={theme}
                />
            </div>

            <div className="flex flex-row w-full h-20 justify-center items-center bg-[#ebebeb] dark:bg-[#111111]">
                <div className="flex flex-row w-full h-full">
                    <div className="flex flex-col flex-1 justify-center items-start mx-3 my-5 border-r border-[#2A282B] gap-1">
                        <div className="flex text-left text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                            Период планирования
                        </div>
                        <div className="flex min-w-[200px] text-left text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                            <select
                                className="form-select w-full focus:outline-none focus:ring-1 focus:ring-[#181818] dark:focus:ring-[#FFFFFF]"
                                value={selectedYear}
                                onChange={handleYearChange}
                            >
                                {availableYears.length === 0 ? (
                                    <option value="Нет данных">
                                        Нет данных
                                    </option>
                                ) : (
                                    availableYears.map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 justify-center items-start mx-3 my-5 border-r border-[#2A282B]">
                        <div className="flex text-left text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                            План закупки
                        </div>
                        <div className="flex text-left text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                            {registrationNumber == 'Нет данных'
                                ? 'Нет данных'
                                : registrationNumber}
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 justify-center items-start mx-3 my-5 border-r border-[#2A282B]">
                        <div className="flex text-left text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                            Количество позиций
                        </div>
                        <div className="flex text-left text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                            {isLoading ? (
                                <svg
                                    className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    ></path>
                                </svg>
                            ) : countPlan == 'Нет данных' ? (
                                'Нет данных'
                            ) : (
                                countPlan
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 justify-center items-start mx-3 my-5 border-r border-[#2A282B]">
                        <div className="flex text-left text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                            Запланировано закупок
                        </div>
                        <div className="flex text-left text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                            {isLoading ? (
                                <svg
                                    className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    ></path>
                                </svg>
                            ) : sumPlan == 'Нет данных' ? (
                                'Нет данных'
                            ) : (
                                <>
                                    <NumberFormat value={Number(sumPlan)} /> ₽
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col flex-1 justify-center items-start mx-3 my-5">
                        <div className="flex text-left text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                            Запланировано закупок у МСП
                        </div>
                        <div className="flex text-left text-[#181818] dark:text-[#FFFFFF] text-[14px]">
                            {isLoading ? (
                                <svg
                                    className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    ></path>
                                </svg>
                            ) : sumMSPPlanPercent == 'Нет данных' ? (
                                'Нет данных'
                            ) : (
                                <>
                                    <NumberFormat value={Number(sumMSPPlan)} />{' '}
                                    ₽{' ('}
                                    {sumMSPPlanPercent} {'%)'}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col w-full h-[calc(100vh_-_176px)] justify-start items-center bg-[#f0f3f5] dark:bg-[#181818] overflow-x-hidden overflow-y-auto custom-scrollbar">
                <div className="flex flex-col bg-[#f0f3f5] dark:bg-[#181818] justify-center items-center space-y-5 p-5">
                    <div className="flex flex-row space-x-5">
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanCount
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanSumm
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanCountMSP
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanSummMSP
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-row space-x-5">
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanCountGWS
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanSummGWS
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanSummType
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[400px] h-60 bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <PieChartPlanCountForm
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    initialIndex={1}
                                    totalIndex={0}
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-row space-x-5">
                        <div className="flex flex-col w-[820px] h-[380px] bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <ColumnChart
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col w-[820px] h-[380px] bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-3 shadow-lg shadow-[#18181825]">
                            {registrationNumber ? (
                                <LineChart
                                    reg_number={
                                        registrationNumber || 'Нет данных'
                                    }
                                    theme={theme}
                                />
                            ) : (
                                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                    <span>Загрузка</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {/* <div className="flex flex-row space-x-5">
                        <div className="flex flex-col w-[540px] h-[400px] bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-2.5">
                            <TableChartFHD reg_number={registrationNumber || 'Нет данных'} />
                        </div>

                        <div className="flex flex-col w-[540px] h-[400px] bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-2.5">
                            <TableChartPurchaseMethod
                                reg_number={registrationNumber || 'Нет данных'}
                            />
                        </div>

                        <div className="flex flex-col w-[540px] h-[400px] bg-[#FFFFFF] dark:bg-[#262427] rounded-lg p-2.5">
                            <TableChartPurchaseStruct
                                reg_number={registrationNumber || 'Нет данных'}
                            />
                        </div>
                    </div> */}
                </div>
                <Footer />
            </div>
        </div>
    )
}

export default HubPage
